import { Theme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles, withStyles } from '@material-ui/styles';
import { FunctionComponent, memo, useState } from 'react';
import Measure, { BoundingRect } from 'react-measure';
import {
  ListDecoratedMedisanteDevice,
  StatusesKeys,
} from '../../../Devices/types';
import { IconsWithEllipsisProps, Props } from './types';
import {
  BehindSyncingSchedule,
  MinBattery,
  MinSignalStrength,
  LowBattery,
  LowSignalStrength,
  NoTargetSystem,
  PendingChanges,
  FlagStatus,
  OKBattery,
  OKSignalStrength,
  MutedDevice,
  TestDeviceInfo,
  LastStatusCode,
} from './IconComponents';
import { Chip } from '@material-ui/core';
import { Block } from '@material-ui/icons';
import { isBluetoothDevice } from '../../types';

const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    marginRight: theme.spacing(1),
    fontSize: '1.43rem',
  },
  tooltipEllipsis: {
    cursor: 'pointer',
    fontSize: '1.5rem',
  },
  iconContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '10px 0',
  },
  stockIcon: {
    fontStyle: 'normal',
    marginLeft: theme.spacing(1),
  },
}));

const CustomTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#dedede',
    boxShadow: theme.shadows[1],
  },
}))(Tooltip);

const IconsWithEllipsis: FunctionComponent<IconsWithEllipsisProps> = ({
  icons,
  maxNumberOfIcons,
}) => {
  const classes = useStyles();

  const main = icons.slice(0, maxNumberOfIcons || icons.length);
  const rest = icons.slice(maxNumberOfIcons, icons.length);

  return (
    <>
      {main}
      <CustomTooltip title={rest} placement="top">
        <span className={classes.tooltipEllipsis}>...</span>
      </CustomTooltip>
    </>
  );
};

const DeviceHealthIcons: FunctionComponent<Props> = memo(
  ({
    device,
    maxNumberOfIcons = 3,
    containerClassName,
    minNumberOfIcons = 4,
    minWidthWithoutEllipsis = 165,
  }) => {
    const [size, setSize] = useState<BoundingRect>();
    const getStatusFlag = (name: StatusesKeys) => {
      return !!device && !!device.statuses && !!device.statuses[name];
    };
    const classes = useStyles();

    const handleStatus = (
      device: ListDecoratedMedisanteDevice,
      value: any,
      elem: JSX.Element[]
    ) => {
      if (isBluetoothDevice(device.model)) {
        return [];
      }
      if (value === undefined || value === false) {
        return [];
      }
      return elem;
    };
    // prettier-ignore
    let icons = [
      ...(getStatusFlag('isBehindSyncingSchedule') ? [<BehindSyncingSchedule key={1} device={device} />] : []),
      ...(getStatusFlag('hasMinBattery') ? [<MinBattery device={device} key={2} />] : handleStatus(device, device.batteryVoltage, [<OKBattery device={device} key={2} />])),
      ...(getStatusFlag('hasMinSignalStrength') ? [<MinSignalStrength device={device} key={3} />] : handleStatus(device, device.signalStrength, [<OKSignalStrength device={device} key={3} />])),
      ...(getStatusFlag('hasLowBattery') ? [<LowBattery device={device} key={4} />] : []),
      ...(getStatusFlag('hasLowSignalStrength') ? [<LowSignalStrength device={device} key={5} />] : []),
      ...(getStatusFlag('hasPendingChanges') ? [<PendingChanges key={6} device={device} />] : []),
      ...(getStatusFlag('hasNoTargetSystem') ? [<NoTargetSystem key={7} />] : []),
      ...(device.properties && device.properties.muted && device.properties.muted.length > 0 ? [<MutedDevice key={8} />] : []),
      ...((device.lastStatusCode > 0 && !device.deactivated) ? [<LastStatusCode device={device} key={9} />] : []),
      ...(device.properties?.testdevice?.length ? [<TestDeviceInfo key={10} />] : []),
      ...(device.inStock ? [<Chip label="Stock" key={11} className={classes.stockIcon} variant="outlined" color="primary" />] : [])
    ];

    const statuses = !device.properties?.status ? (
      []
    ) : (
      <FlagStatus statuses={device.properties.status} key={12} />
    );

    icons = icons.concat(statuses);

    if (device.deactivated) {
      icons.push(
        <Tooltip
          title="Device blocked - measurement blocking in place"
          className={classes.icon}
        >
          <Block color="error" />
        </Tooltip>
      );
    }
    return device.isArchived ? (
      <Chip label="Archived" variant="outlined" color="primary" />
    ) : (
      <>
        <Measure
          bounds
          onResize={(contentRect) => {
            setSize(contentRect.bounds);
          }}
        >
          {({ measureRef }) => (
            <i
              className={`${classes.iconContainer} ${containerClassName || ''}`}
              ref={measureRef}
            >
              {(!size || size.width < minWidthWithoutEllipsis) &&
              icons.length >= minNumberOfIcons ? (
                <IconsWithEllipsis
                  icons={icons}
                  maxNumberOfIcons={maxNumberOfIcons}
                />
              ) : (
                icons
              )}
            </i>
          )}
        </Measure>
      </>
    );
  }
);

export default DeviceHealthIcons;
